
import { defineComponent, ref, onMounted } from "vue"
import { Actions } from "@/store/enums/StoreEnums"
import { useStore } from "vuex"
import { useRoute } from "vue-router"
import { setCurrentPageBreadcrumbs } from "@/core/helpers/breadcrumb"
import { ElMessage, ElMessageBox } from "element-plus"
import { CardPool } from "@/store/modules/CardPoolModule"
import { CardSeriesOption } from "@/store/modules/CardSeriesModule"
import ProbabilityPreview from "./ProbabilityPreview.vue"
import SimulationModal from "./SimulationModal.vue"

export default defineComponent({
  components: {
    ProbabilityPreview,
    SimulationModal
  },
  setup() {
    onMounted(() => {
      setCurrentPageBreadcrumbs("卡池列表", ["抽卡系统"])
      loadOptions()
    })

    const store = useStore()
    const route = useRoute()
    const tableData = ref<CardPool[]>([])
    const cardSeriesOptions = ref<CardSeriesOption[]>([])

    const formData = ref({
      name: '',
      card_series_id: (route.query.card_series_id as string) || '',
      enabled: ''
    })

    const page = ref({
      currentPage: 1,
      pageSize: 10,
      totalResult: 0,
    })

    // 概率预览相关
    const probabilityDialogVisible = ref(false)
    const currentPool = ref<CardPool | null>(null)

    // 模拟相关
    const simulationDialogVisible = ref(false)
    const simulationPool = ref<CardPool | null>(null)

    const loadOptions = () => {
      store.dispatch(Actions.GET_ALL_CARD_SERIES).then(() => {
        cardSeriesOptions.value = store.getters.currentAllCardSeries
      })
    }

    const query = () => {
      store
        .dispatch(Actions.GET_CARD_POOLS, { ...formData.value, ...page.value })
        .then(() => {
          tableData.value = store.getters.currentCardPoolsList
          page.value.totalResult = store.getters.currentCardPoolsCount
        })
        .catch(() => {
          ElMessage.error('获取卡池列表失败')
        })
    }

    query()

    const submitEvent = () => {
      page.value.currentPage = 1
      query()
    }

    const onPageChange = (e: { currentPage: number; pageSize: number }) => {
      page.value.currentPage = e.currentPage
      page.value.pageSize = e.pageSize
      query()
    }

    const onReset = () => {
      formData.value = {
        name: '',
        card_series_id: '',
        enabled: ''
      }
      page.value.currentPage = 1
      query()
    }

    const onViewProbability = (row: CardPool) => {
      currentPool.value = row
      probabilityDialogVisible.value = true
    }

    const onSimulate = (row: CardPool) => {
      simulationPool.value = row
      simulationDialogVisible.value = true
    }

    const onDuplicate = (row: CardPool) => {
      ElMessageBox.confirm(
        `确定要复制卡池「${row.name}」吗？将创建一个停用状态的副本。`,
        '复制确认',
        {
          confirmButtonText: '确定',
          cancelButtonText: '取消',
          type: 'info',
        }
      )
        .then(() => {
          store
            .dispatch(Actions.DUPLICATE_CARD_POOL, row.id)
            .then((data: { success: boolean; error_msg?: string; card_pool?: CardPool }) => {
              if (data.success) {
                ElMessage.success('复制成功')
                query()
              } else {
                ElMessage.error(data.error_msg || '复制失败')
              }
            })
            .catch(() => {
              ElMessage.error('复制失败')
            })
        })
        .catch(() => {
          // 取消
        })
    }

    const onArrive = (row: CardPool) => {
      ElMessageBox.confirm(
        `确定要标记卡池「${row.name}」为已到货吗？到货后用户可以发货。`,
        '到货确认',
        {
          confirmButtonText: '确定',
          cancelButtonText: '取消',
          type: 'warning',
        }
      )
        .then(() => {
          store
            .dispatch(Actions.ARRIVE_CARD_POOL, row.id)
            .then((data: { success: boolean; error_msg?: string }) => {
              if (data.success) {
                ElMessage.success('已标记到货')
                query()
              } else {
                ElMessage.error(data.error_msg || '操作失败')
              }
            })
            .catch(() => {
              ElMessage.error('操作失败')
            })
        })
        .catch(() => {
          // 取消
        })
    }

    const onDelete = (row: CardPool) => {
      ElMessageBox.confirm(
        `确定要删除卡池「${row.name}」吗？`,
        '删除确认',
        {
          confirmButtonText: '确定',
          cancelButtonText: '取消',
          type: 'warning',
        }
      )
        .then(() => {
          store
            .dispatch(Actions.DELETE_CARD_POOL, row.id)
            .then((data: { success: boolean; error_msg?: string }) => {
              if (data.success) {
                ElMessage.success('删除成功')
                query()
              } else {
                ElMessage.error(data.error_msg || '删除失败')
              }
            })
            .catch(() => {
              ElMessage.error('删除失败')
            })
        })
        .catch(() => {
          // 取消删除
        })
    }

    return {
      tableData,
      formData,
      cardSeriesOptions,
      submitEvent,
      page,
      onPageChange,
      onReset,
      onViewProbability,
      onDelete,
      onDuplicate,
      onArrive,
      probabilityDialogVisible,
      currentPool,
      simulationDialogVisible,
      simulationPool,
      onSimulate
    }
  },
})
